import { makeAutoObservable } from "mobx";
import { RootStore, LangStore } from "../../../store";
import { StoreStatus, CallbackDataParams } from "../../../store/types";
import { getApi, getApiError, isSessionExpired } from "../../../api";
import { ViewEntityStore } from "../../common/store/types";
import { esignatureWebhookEventService } from "../services/esignatureWebhookEventService";
import { esignatureWebhookEventDisplayId } from "./EsignatureWebhookEventCommonStore";
import { EsignatureWebhookEvent } from "../../../api/graphql";
import { EsignatureProvider } from "../../../api/graphql";
import { InitializeParams } from "../../../store/types";

export class EsignatureWebhookEventViewStore implements ViewEntityStore {
  readonly rootStore: RootStore;
  readonly langStore: LangStore;
  status: StoreStatus = "uninitialized";
  storeId?: string;
  originalError: any;
  errorDialogOpened: boolean = false;
  errorTitle: string = "Error";
  errorDescription: string = "Hubo un error";
  service = esignatureWebhookEventService;
  esignatureWebhookEventInstance: EsignatureWebhookEvent | null = null;
  cardState: Record<string, boolean> = {};
  savedId: string | null = null;
  cancelled = false;
  isDrawerMode = false;
  isModalMode = false;
  callbackParams: CallbackDataParams | null = null;
  idValue: string | undefined;
  providerValue: EsignatureProvider = "" as EsignatureProvider;
  externalEventIdValue: string = "";
  eventTypeValue: string = "";
  documentExternalIdValue: string = "";
  processedAtValue: Date | null = null;
  errorValue: string = "";

  constructor(rootStore: RootStore, storeId?: string) {
    this.rootStore = rootStore;
    this.langStore = rootStore.langStore;
    this.storeId = storeId;

    makeAutoObservable(this);
  }

  destroy() {
    // this object will be unusable
  }

  setCallbackParams(callbackParams: CallbackDataParams | null) {
    if (callbackParams?.callerData) {
      this.callbackParams = callbackParams;
    }
  }

  toggleCardState(id: string) {
    this.cardState[id] = !this.cardState[id];
  }

  setCardState(id: string, status: boolean) {
    this.cardState[id] = status;
  }

  getCardState(id: string) {
    if (!(id in this.cardState)) {
      this.cardState[id] = true;
    }
    return !!this.cardState[id];
  }

  *loadEsignatureWebhookEventById(id: string): any {
    const api = yield* getApi(
      this.rootStore.authStore,
      this.rootStore.activityStore
    );
    const result = yield api.esignatureWebhookEventOne({ id });
    this.esignatureWebhookEventInstance = result.esignatureWebhookEventOne;
    if (this.esignatureWebhookEventInstance) {
      this.loadValues(this.esignatureWebhookEventInstance);
    }
  }

  setStatus(status: StoreStatus) {
    this.status = status;
  }

  get processingTitle() {
    return this.langStore.processingTitle;
  }

  get processingDescription() {
    return this.langStore.processingDescription;
  }

  get displayId(): string {
    if (this.esignatureWebhookEventInstance) {
      return esignatureWebhookEventDisplayId(
        this.esignatureWebhookEventInstance,
        this.langStore
      );
    }
    return "";
  }

  get breadcrumbLabel(): string {
    const displayId = this.displayId;
    if (displayId) {
      return displayId;
    }
    return this.langStore.EsignatureWebhookEventSingular;
  }

  closeErrorDialog() {
    this.errorDialogOpened = false;
  }

  setContentTop(_contentTop: number) {}

  returnedData(_params: CallbackDataParams): any {}

  showErrorDialog(originalError?: any, title?: string, description?: string) {
    // isSessionExpired y no una comparación a mano: los llamadores pasan a
    // veces el Error crudo y a veces el código ya resuelto, y cada store tenía
    // su propia versión del chequeo —o ninguna—, así que la sesión vencida se
    // detectaba o no según por dónde hubiera entrado el error.
    if (isSessionExpired(originalError, this.langStore)) {
      this.rootStore.authStore.openSessionExpiredDialog(() => {});
      return;
    }

    this.originalError = originalError;
    if (title) {
      this.errorTitle = title;
    }
    if (description) {
      this.errorDescription = description;
    }
    this.errorDialogOpened = true;
  }

  getProviderOptions() {
    return this.langStore.EsignatureProviderOptions;
  }

  clearFields() {
    this.savedId = null;
    this.cancelled = false;
    this.esignatureWebhookEventInstance = null;
    this.providerValue = "" as EsignatureProvider;
    this.externalEventIdValue = "";
    this.eventTypeValue = "";
    this.documentExternalIdValue = "";
    this.processedAtValue = null;
    this.errorValue = "";
  }

  close() {
    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      // In modal mode, close or pop the modal instead of navigating
      if (this.rootStore.modalViewStack.length > 1) {
        this.rootStore.popModalView();
      } else {
        this.rootStore.closeModalView();
      }
    } else {
      this.rootStore.router.goBack();
    }
  }

  cancelEsignatureWebhookEvent() {
    this.cancelled = true;
    this.close();
  }

  get isEditEnabled(): boolean {
    return false;
  }

  editEsignatureWebhookEvent() {
    const id = this.esignatureWebhookEventInstance?.id;
    if (!id) return;

    if (this.isDrawerMode) {
      this.rootStore.closeDrawerView();
    } else if (this.isModalMode) {
      this.rootStore.closeModalView();
    }
    this.rootStore.router.goTo(
      "/esignatureWebhookEvent/update/" + id,
      undefined,
      undefined,
      undefined,
      this.breadcrumbLabel
    );
  }

  printEsignatureWebhookEvent(handlePrint: () => void): void {
    handlePrint();
  }

  *closeEsignatureWebhookEvent(): any {
    yield this.close();
  }

  loadValues(instance: EsignatureWebhookEvent) {
    this.idValue = instance.id;
    this.providerValue = instance.provider;
    this.externalEventIdValue = instance.externalEventId;
    this.eventTypeValue = instance.eventType;
    this.documentExternalIdValue = instance.documentExternalId || "";
    this.processedAtValue = instance.processedAt
      ? new Date(instance.processedAt)
      : null;
    this.errorValue = instance.error || "";
  }

  getState(): InitializeParams {
    return {
      id: this.idValue,
    };
  }

  *initialize(params?: InitializeParams) {
    try {
      this.status = "processing";

      const id = params?.id;

      if (id) {
        yield this.loadEsignatureWebhookEventById(id);
      }

      this.status = "ok";
    } catch (error) {
      this.status = "error";
      if (error instanceof Error) {
        const apiError = getApiError(error, this.langStore);
        let message = apiError.message;
        this.showErrorDialog(error, apiError.title, message);
      } else {
        this.showErrorDialog(error, this.langStore.serverError, undefined);
      }
    }
  }
}
